import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { 
  Send, 
  Search, 
  MessageCircle
} from 'lucide-react';

interface ChatRoom {
  id: number;
  user1: {
    id: number;
    username: string;
    fullName: string;
    profilePhotoUrl?: string;
  };
  user2: {
    id: number;
    username: string;
    fullName: string;
    profilePhotoUrl?: string;
  };
  skill?: {
    id: number;
    name: string;
  };
  lastMessageAt?: string;
  createdAt: string;
}

interface ChatRoomWithMetadata {
  room: ChatRoom;
  otherUser: {
    id: number;
    username: string;
    fullName: string;
    profilePhotoUrl?: string;
  };
  unreadCount: number;
}

interface ChatMessage {
  id: number;
  content: string;
  sentAt: string;
  sender: {
    id: number;
    username: string;
    fullName: string;
  };
  isRead: boolean;
  readAt?: string;
}



const Chat: React.FC = () => {
  const { user } = useAuth();
  const [chatRooms, setChatRooms] = useState<ChatRoomWithMetadata[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoomWithMetadata | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      fetchChatRooms();
    }
  }, [user]);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.room.id);
      markMessagesAsRead(selectedRoom.room.id);
    }
  }, [selectedRoom]);

  // Poll for new messages every 3 seconds when a room is selected
  useEffect(() => {
    if (!selectedRoom) return;

    const interval = setInterval(() => {
      fetchMessages(selectedRoom.room.id);
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedRoom]);

  // Poll for new messages in all chat rooms every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (chatRooms.length > 0) {
        fetchChatRooms();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [chatRooms.length]);

  useEffect(() => {
    setupWebSocket();
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const setupWebSocket = () => {
    if (!selectedRoom) return;

    // Check if WebSocket is supported
    if (!('WebSocket' in window)) {
      console.warn('WebSocket is not supported in this browser');
      return;
    }

    // For now, we'll disable WebSocket connections until the backend is properly configured
    // This prevents the error messages you're seeing
    console.log('WebSocket connection disabled - backend WebSocket service not yet configured');
    return;

    // TODO: Implement proper STOMP WebSocket connection when backend is ready
    // const wsUrl = `ws://localhost:8080/ws`;
    // const websocket = new WebSocket(wsUrl);
    // ... STOMP implementation
  };





  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const fetchChatRooms = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getChatRooms();
      setChatRooms(response.data);
    } catch (error: any) {
      toast.error('Failed to load chat rooms');
      console.error('Error fetching chat rooms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async (roomId: number) => {
    try {
      const response = await apiService.getChatMessages(roomId);
      setMessages(response.data);
    } catch (error: any) {
      toast.error('Failed to load messages');
      console.error('Error fetching messages:', error);
    }
  };

  const handleIncomingMessage = (message: ChatMessage, roomId: number) => {
    // Update messages if we're in the current room
    if (selectedRoom && selectedRoom.room.id === roomId) {
      setMessages(prev => {
        // Check if message already exists to avoid duplicates
        const messageExists = prev.some(msg => msg.id === message.id);
        if (messageExists) return prev;
        return [...prev, message];
      });
    }

    // Update chat rooms to show new message
    setChatRooms(prev => prev.map(roomData => 
      roomData.room.id === roomId 
        ? { 
            ...roomData, 
            room: { 
              ...roomData.room, 
              lastMessageAt: message.sentAt 
            },
            unreadCount: selectedRoom?.room.id === roomId ? 0 : roomData.unreadCount + 1
          }
        : roomData
    ));
  };

  const markMessagesAsRead = async (roomId: number) => {
    try {
      await apiService.markMessagesAsRead(roomId);
      setChatRooms(prev => prev.map(roomData => 
        roomData.room.id === roomId ? { ...roomData, unreadCount: 0 } : roomData
      ));
    } catch (error: any) {
      console.error('Error marking messages as read:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom || isSending) return;

    const messageContent = newMessage.trim();
    const otherUser = getOtherParticipant(selectedRoom);
    
    if (!otherUser) return;

    setIsSending(true);

    // Create optimistic message for immediate UI update
    const optimisticMessage: ChatMessage = {
      id: Date.now(), // Temporary ID
      content: messageContent,
      sentAt: new Date().toISOString(),
      sender: {
        id: user!.id,
        username: user!.username,
        fullName: user!.fullName || user!.username
      },
      isRead: false
    };

    // Add message to local state immediately
    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');

    try {
      const messageData = {
        chatRoomId: selectedRoom.room.id,
        recipientId: otherUser.id,
        content: messageContent
      };

      const response = await apiService.sendMessage(messageData);
      
      // Update the message with the real ID from the server
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticMessage.id 
          ? { ...msg, id: response.data.id || msg.id }
          : msg
      ));

      // Update chat rooms to show this room as most recent
      setChatRooms(prev => prev.map(roomData => 
        roomData.room.id === selectedRoom.room.id 
          ? { 
              ...roomData, 
              room: { 
                ...roomData.room, 
                lastMessageAt: new Date().toISOString() 
              } 
            }
          : roomData
      ));

    } catch (error: any) {
      // Remove the optimistic message if sending failed
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      toast.error('Failed to send message');
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };



  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getOtherParticipant = (roomData: ChatRoomWithMetadata) => {
    return roomData.otherUser;
  };

  const filteredRooms = chatRooms.filter(roomData => {
    const otherUser = getOtherParticipant(roomData);
    return otherUser?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           otherUser?.username?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const renderMessage = (message: ChatMessage) => {
    const isOwnMessage = message.sender.id === user?.id;

    return (
      <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-2' : 'order-1'}`}>
          {!isOwnMessage && (
            <div className="flex items-center mb-2">
              <div className="h-6 w-6 rounded-full bg-gradient-to-r from-blue-100 to-teal-100 flex items-center justify-center mr-2">
                <span className="text-blue-600 text-xs font-medium">
                  {message.sender.username.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-xs text-gray-500 font-medium">{message.sender.fullName || message.sender.username}</span>
            </div>
          )}
          
          <div className={`rounded-xl px-4 py-3 shadow-sm ${
            isOwnMessage 
              ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white' 
              : 'bg-gradient-to-r from-gray-50 to-blue-50 text-gray-900 border border-blue-100'
          }`}>
            <p className="text-sm">{message.content}</p>
            <div className={`text-xs mt-2 ${
              isOwnMessage ? 'text-blue-100' : 'text-gray-500'
            }`}>
              {formatTimestamp(message.sentAt)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4 pulse-glow"></div>
          <h2 className="text-xl font-bold gradient-text">Loading Chat...</h2>
          <p className="text-gray-600">Preparing your conversations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Chat Rooms Sidebar */}
      <div className="w-80 bg-white border-r border-blue-200 flex flex-col shadow-lg">
        <div className="p-6 border-b border-blue-200 bg-gradient-to-r from-blue-50 to-teal-50">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Messages</h2>
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02]"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredRooms.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="h-16 w-16 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <MessageCircle className="h-8 w-8 text-blue-600" />
              </div>
              <p className="text-lg">No conversations found</p>
            </div>
          ) : (
            filteredRooms.map((roomData) => {
              const otherUser = getOtherParticipant(roomData);
              return (
                <div
                  key={roomData.room.id}
                  onClick={() => setSelectedRoom(roomData)}
                  className={`p-4 border-b border-blue-100 cursor-pointer hover:bg-blue-50 transition-all duration-300 ${
                    selectedRoom?.room.id === roomData.room.id ? 'bg-gradient-to-r from-blue-50 to-teal-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 flex items-center justify-center shadow-lg">
                      {otherUser?.profilePhotoUrl ? (
                        <img
                          src={otherUser.profilePhotoUrl}
                          alt={otherUser.fullName || otherUser.username}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-sm font-medium">
                          {(otherUser?.fullName || otherUser?.username || 'U').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {otherUser?.fullName || otherUser?.username}
                        </h3>
                        {roomData.unreadCount > 0 && (
                          <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                            {roomData.unreadCount}
                          </span>
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 flex flex-col bg-gradient-to-br from-blue-50 via-white to-teal-50">
        {selectedRoom ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-blue-200 p-6 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 flex items-center justify-center shadow-lg">
                  {getOtherParticipant(selectedRoom)?.profilePhotoUrl ? (
                    <img
                      src={getOtherParticipant(selectedRoom)?.profilePhotoUrl}
                      alt={getOtherParticipant(selectedRoom)?.fullName || getOtherParticipant(selectedRoom)?.username}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-sm font-medium">
                      {(getOtherParticipant(selectedRoom)?.fullName || getOtherParticipant(selectedRoom)?.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {getOtherParticipant(selectedRoom)?.fullName || getOtherParticipant(selectedRoom)?.username}
                  </h3>
                  <p className="text-sm text-gray-500">Active now</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map(renderMessage)}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="bg-white border-t border-blue-200 p-6 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="flex-1 relative group">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={handleMessageChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-300 focus:scale-[1.02]"
                  />
                </div>
                
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || isSending}
                  className="p-3 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl hover:from-blue-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                >
                  {isSending ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span className="text-sm">Sending...</span>
                    </div>
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="h-20 w-20 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <MessageCircle className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Select a conversation</h3>
              <p className="text-gray-500 text-lg">Choose a conversation from the sidebar to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat; 